<script setup lang="ts">
import type { VEmptyStateKind } from "./VEmptyState.types";
import { computed } from "vue";
import VEmptyIllustration from "./VEmptyIllustration.vue";

defineOptions({
  name: "VEmptyState",
});

/**
 * An empty list or tab: illustration, title, text and an optional action, centred. `card` draws the white bordered
 * panel of the boards; `dashed` the dashed placeholder of a list section that has nothing yet (a marketplace
 * category); `plain` leaves the surface to the parent (e.g. inside a table body).
 *
 * With `state` the illustration follows the empty-state illustration set: the scene of the screen (the `scene` slot,
 * or the generic list scene) with the badge of that state. The `illustration` slot replaces it completely.
 */
const {
  title = "",
  text = "",
  variant = "card",
  headingLevel = 2,
  fillHeight = false,
  state = undefined,
  live = undefined,
  detail = "",
} = defineProps<{
  title?: string
  text?: string
  variant?: "card" | "dashed" | "plain"
  headingLevel?: 2 | 3
  /** Stretches to the height of the parent and centres the content vertically. */
  fillHeight?: boolean
  /** Draws the state illustration (200x140) with this state's badge, unless the `illustration` slot is used. */
  state?: VEmptyStateKind
  /**
   * Announces the state when it appears: `status` for a result that changed by a search or filter, `alert` for an
   * error. Leave it out for a list that is simply empty on arrival.
   */
  live?: "status" | "alert"
  /** A small technical line under the text, such as an error code, for support. */
  detail?: string
}>();

const slots = defineSlots<{
  /** The whole illustration, such as the 120x84 empty-state scene from the App Store illustration set. */
  illustration?: () => unknown
  /** The scene of this screen as SVG elements in the 200x140 canvas; used with `state`. */
  scene?: () => unknown
  /** Rich title instead of `title`. */
  title?: () => unknown
  /** Rich text instead of `text`; may hold block content. */
  default?: () => unknown
  /** One button, primary or gray as on the board. */
  actions?: () => unknown
}>();

const VARIANT_CLASS: Record<"card" | "dashed" | "plain", string> = {
  card: "rounded-[14px] border border-gray-200 bg-white p-8 dark:border-dark-600 dark:bg-dark-800",
  dashed: "rounded-2xl border border-dashed border-gray-300 px-6 py-16 dark:border-dark-600",
  plain: "px-4 py-8",
};

const rootClass = computed(() => [
  VARIANT_CLASS[variant],
  fillHeight ? "h-full min-h-full" : undefined,
]);
const headingTag = computed(() => `h${headingLevel}`);
// The dashed section placeholder carries a smaller, lighter title than a full empty panel.
const headingClass = computed(() => (variant === "dashed" ? "text-base font-medium" : "text-lg font-bold"));
// The dashed placeholder stacks its tile, title and text 10px apart; the full panels keep the wider rhythm.
const stackGapClass = computed(() => (variant === "dashed" ? "gap-2.5" : "gap-3.5"));
const textGapClass = computed(() => (variant === "dashed" ? "gap-2.5" : "gap-1.5"));
// A table body is wider than a card, so its text may run longer before it wraps.
const textWidthClass = computed(() => (variant === "plain" ? "max-w-[600px]" : "max-w-[380px]"));

function showStateIllustration(): boolean {
  return !slots.illustration && state !== undefined;
}
</script>

<template>
  <div
    class="flex flex-col items-center justify-center text-center"
    :class="[rootClass, stackGapClass]"
    :role="live"
    :data-state="state"
  >
    <span v-if="slots.illustration" class="flex shrink-0" aria-hidden="true">
      <slot name="illustration" />
    </span>
    <span v-else-if="showStateIllustration()" class="flex shrink-0" aria-hidden="true">
      <VEmptyIllustration :state="state">
        <slot v-if="slots.scene" name="scene" />
      </VEmptyIllustration>
    </span>
    <div class="flex flex-col" :class="[textWidthClass, textGapClass]">
      <component :is="headingTag" class="m-0 text-balance text-(--text)" :class="headingClass">
        <slot name="title">
          {{ title }}
        </slot>
      </component>
      <component
        :is="slots.default ? 'div' : 'p'"
        v-if="text || slots.default"
        class="m-0 text-sm leading-normal text-pretty text-gray-700 dark:text-gray-400"
      >
        <slot>{{ text }}</slot>
      </component>
      <p v-if="detail" class="m-0 font-mono text-xs text-gray-600 dark:text-gray-500" data-testid="empty-state-detail">
        {{ detail }}
      </p>
    </div>
    <div v-if="slots.actions" class="flex flex-wrap items-center justify-center gap-2.5">
      <slot name="actions" />
    </div>
  </div>
</template>
